import { BudgetItem } from "./types";

export interface BudgetReorderEntry {
  item_id: number;
  sort_order: number;
  parent_item_id?: number | null;
}

export type BudgetLineMoveDirection = -1 | 1;

/**
 * Swap a line with its neighbor in the given direction (-1 up, 1 down).
 * Returns the reordered siblings, or `null` when the move is not possible
 * (item missing, or already at the boundary).
 */
export function moveBudgetLine(
  items: BudgetItem[],
  itemId: number,
  direction: BudgetLineMoveDirection
): BudgetItem[] | null {
  const index = items.findIndex((item) => item.budget_item_id === itemId);
  if (index === -1) return null;

  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= items.length) return null;

  const next = [...items];
  const [moved] = next.splice(index, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

/**
 * Map an ordered list of siblings to the reorder payload the API expects,
 * reindexing `sort_order` from the array position. `parentItemId` is always
 * sent (as `null` for top-level lines) so the server keeps the line's parent.
 */
export function buildReorderPayload(
  items: BudgetItem[],
  parentItemId: number | null
): BudgetReorderEntry[] {
  // The API validates `parent_item_id` as a positive int (or absent), so any
  // non-positive value (e.g. the sub-category view's `0`) must become `null`.
  const parent = parentItemId && parentItemId > 0 ? parentItemId : null;
  return items.map((item, index) => ({
    item_id: item.budget_item_id,
    sort_order: index,
    parent_item_id: parent,
  }));
}

export function canMoveBudgetLineUp(items: BudgetItem[], itemId: number): boolean {
  return items.findIndex((item) => item.budget_item_id === itemId) > 0;
}

export function canMoveBudgetLineDown(items: BudgetItem[], itemId: number): boolean {
  const index = items.findIndex((item) => item.budget_item_id === itemId);
  return index !== -1 && index < items.length - 1;
}
